import {usePrefersReducedMotion} from '@dropbox/dig-foundations';
import {type ReactNode, useEffect, useLayoutEffect, useRef, useState} from 'react';

/**
 * Fades `element` in, settling down 4px onto what's above it; quick, so opening
 * never feels like waiting. For content that stays mounted while hidden (FadeIn
 * covers what mounts). Callers skip it under reduced motion.
 */
export const fadeIn = (element: HTMLElement | null) => {
  // jsdom has no Web Animations.
  element?.animate?.(
    [
      {opacity: 0, transform: 'translateY(-4px)'},
      {opacity: 1, transform: 'none'},
    ],
    {duration: 100, easing: 'ease-out'}
  );
};

/**
 * False on a component's first render, true after: what opens after the page
 * arrived (so FadeIn's `enabled`) rather than with it.
 */
export const useHasMounted = () => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  return mounted;
};

/**
 * /workstreams: fades its children in as it mounts (see fadeIn): a
 * card's body under its header, a fold's contents under its toggle, if
 * `enabled` then. Pass false for what started open, so a page doesn't arrive
 * animating. Only the mount counts, so a later flip of `enabled` doesn't replay
 * it. Off under reduced motion.
 */
export const FadeIn = ({enabled = true, children}: {enabled?: boolean; children: ReactNode}) => {
  const ref = useRef<HTMLDivElement>(null);
  const {hasReducedMotion} = usePrefersReducedMotion();
  const animateRef = useRef(enabled && !hasReducedMotion);
  useLayoutEffect(() => {
    if (animateRef.current) fadeIn(ref.current);
  }, []);
  // Shrinkable, so wide content inside a flex parent still fits.
  return (
    <div ref={ref} style={{minWidth: 0}}>
      {children}
    </div>
  );
};
